'use server'

import { apiRequest, ApiResponse } from '../request'
import { getClient } from '../client'
import { deliverPdf, downloadBlob, PdfDelivery } from '../../download-blob'
import type { CancelAppointmentResult } from '../exams/exam-api'
import type { PayoutShare } from '@/lib/billing/financials'
import { getStoredToken } from '@/lib/api/session-refresh'
import type { ServiceAddressSnapshot } from '@/lib/api/common/service-address'

export type OrderStatus =
    | 'BUDGET'
    | 'SCHEDULED'
    | 'IN_PROGRESS'
    | 'COMPLETED'
    | 'CANCELLED'
    | 'POSTPONED'

export interface OrderAttachment {
    fileName: string
    filePath: string
    userName: string
    description: string
    details?: string | null
    createdAt?: string
}

export interface OrderTeamMember {
    id: number
    orderId: number
    professionalId: number
    professional?: {
        id: number
        name: string
        registrationNumber?: string
    }
    role?: string
    createdAt: string
    updatedAt: string
}

export interface Order {
    id: number
    /**
     * Nome do pedido: snapshot dos itens empilhados. É o que a listagem mostra quando não
     * há `catalog` — caso da venda de loja, que não tem item âncora.
     */
    title?: string | null
    status: OrderStatus
    /**
     * Pagamento flex: aprovado sem cobrança; o cliente quita em parcelas
     * avulsas pelo portal (/client).
     */
    flexPayment?: boolean
    discount?: number
    description?: string
    notes?: string
    scheduledAt?: string
    startedAt?: string
    completedAt?: string
    /** Cancelamento: o registro não é excluído, fica listado como Cancelado. */
    cancelledAt?: string | null
    cancellationReason?: string | null
    duration?: number
    room?: string
    equipment?: string
    anesthetistFee?: number | null
    /** Quem recebe a taxa do anestesista. Ver docs/billing-system.md §7. */
    anesthetistRecipientKind?: 'HOSPITAL' | 'PROFESSIONAL' | 'PARTNER' | null
    anesthetistRecipientId?: number | null
    hospitalFee?: number | null
    hospitalFeeNotes?: string | null
    validUntil?: string | null
    /** Vencimento da guia renovado manualmente pelo operador ("Renovar Guia"). */
    guideValidUntil?: string | null
    approvedAt?: string | null
    approvedBy?: number | null
    approver?: { id: number; name: string } | null
    preInstructions?: string
    postInstructions?: string
    complications?: string
    outcome?: string
    medicalSupplies?: {
        id: number
        medicalSupplyId: number
        medicalSupply: { id: number; name: string; type: string; unit: string; quantity: number; costPrice: number; sellingPrice?: number | null }
        quantity: number
        unitPrice: number
        /** Custo congelado — o que vira repasse. */
        costPriceSnapshot?: number | null
        /** `SUPPLIER` (padrão) ou `HOSPITAL`, quando o material sai do hospital. */
        recipientKind?: 'SUPPLIER' | 'HOSPITAL' | 'PROFESSIONAL' | 'PARTNER' | null
        recipientId?: number | null
        notes?: string | null
    }[]
    clientId: number
    client?: {
        id: number
        name: string
        cpf?: string
        email?: string
        whatsAppNumber?: string
    }
    professionalId?: number
    professional?: {
        id: number
        name: string
        registrationNumber?: string
    }
    createdById?: number
    createdBy?: {
        id: number
        name: string
        email?: string
    }
    partnerId?: number
    partner?: {
        id: number
        name: string
        tradingName?: string
    }
    healthPlanId?: number
    healthPlan?: { id: number; name: string; ansCode?: string }
    hospitalId?: number | null
    hospital?: {
        id: number
        name: string
        tradingName?: string | null
        cnpj?: string | null
    } | null
    companyId: number
    catalogId: number
    // Quando o agendamento nasceu de um PACOTE, guarda o pacote de origem.
    // Null = procedimento comum.
    packageCatalogId?: number | null
    packageCatalog?: { id: number; name: string } | null
    items?: {
        catalogId: number
        quantity: number
        unitPrice: number
        catalogVariationId?: number | null
        variationName?: string | null
        catalogVariation?: { id: number; name: string } | null
        catalog: {
            id: number
            name: string
            catalogType?: { id: number; name: string } | null
        }
    }[]
    catalog: {
        id: number
        name: string
        catalogType?: { id: number; name: string }
        description?: string
        defaultDuration?: number
        price?: number
        sellingPrice?: number
        markupPercentage?: number
        maxDiscount?: number
    }
    totalPrice?: number | null
    /**
     * O valor COBRADO (já com desconto). `totalPrice` é o de antes dele — usar um
     * pelo outro faz "quanto falta pagar" nascer maior do que a dívida real.
     */
    finalPrice?: number | null
    attachments?: OrderAttachment[]
    metadata?: any
    team?: OrderTeamMember[]
    exams?: {
        id: number
        catalogId: number
        catalog: {
            id: number
            name: string
            category: string
            price: number
            sellingPrice?: number | null
            catalogType?: { id: number; name: string } | null
        }
        quantity: number
        unitPrice: number
        complimentary: boolean
        notes?: string | null
        // Variação escolhida para este atrelado (uma por linha; múltiplas variações
        // do mesmo item viram linhas próprias). Ver OrderLinkedCatalog.
        catalogVariationId?: number | null
        variationName?: string | null
    }[]
    billingId?: number | null
    billing?: {
        id: number
        status: string
        amount: number
        paidAmount?: number | null
        paymentMethod?: string | null
        dueDate: string
        paidAt?: string | null
        publicToken?: string | null
        description?: string | null
        pixQrCodeBase64?: string | null
        pixCopyPaste?: string | null
        bankSlipUrl?: string | null
        barcode?: string | null
        digitableLine?: string | null
        paymentLink?: string | null
        /** Cobrança gerada no gateway: habilita a consulta ao provedor antes da baixa. */
        providerPaymentId?: string | null
        refundedAmount?: number | null
        refundedAt?: string | null
        refundedVia?: string | null
        /**
         * Cobranças penduradas nesta: complemento de ajuste de valor e, no
         * pagamento flex, cada parcela avulsa que o paciente gerou pelo portal.
         * Sem elas, "quanto já entrou" só enxerga a primeira.
         */
        adjustments?: {
            id: number
            status: string
            amount: number
            paidAmount?: number | null
            refundedAmount?: number | null
        }[] | null
        /**
         * Rateio do repasse por atendimento. Presente quando a cobrança cobre mais
         * de um (o paciente pagou a visita inteira de uma vez): aí os valores desta
         * tela têm de sair da FATIA, não da cobrança, que vale pelos outros também.
         * Ver docs/billing-payout-share.md.
         */
        payoutShares?: PayoutShare[] | null
    } | null
    dealId?: number
    deal?: {
        id: number
        title: string
        status: string
        totalValue?: number
        stage: { id: number; name: string; color?: string }
        pipeline: { id: number; name: string; type: string }
        lead: { id: number; name: string; phoneNumber?: string; email?: string }
    }
    // === LOCAL (snapshot) ===
    // No atendimento é o LOCAL onde ele acontece; no pedido de loja, o endereço de
    // ENTREGA. Congelado no registro: corrigir o cadastro depois não reescreve o que já
    // foi comunicado.
    street?: string | null
    number?: string | null
    complement?: string | null
    neighborhood?: string | null
    city?: string | null
    state?: string | null
    zipCode?: string | null
    country?: string | null
    addressSource?: 'PARTNER' | 'PROFESSIONAL' | 'HOSPITAL' | 'CUSTOM' | 'UNCONFIRMED' | null

    // === VENDA DE MERCADORIA (loja e balcão) ===
    // Os campos de loja abaixo são preenchidos só na venda da vitrine (`origin = 'STORE'`);
    // a venda de balcão (`COUNTER`) não tem loja, cupom nem frete. Ver
    // docs/store-checkout-stock.md.
    /**
     * De onde o pedido nasceu. Pedido do operador na agenda é `ADMIN`.
     *
     * `STORE` = vitrine, `COUNTER` = venda de balcão no painel, `AI` = agente no WhatsApp.
     * Os dois primeiros são MERCADORIA: não têm executante nem quem comparecer, e as telas
     * recortam por isso (ver `isGoodsSale` no detalhe do pedido).
     */
    origin?: 'ADMIN' | 'STORE' | 'AI' | 'COUNTER'
    storeConfigId?: number | null
    storeConfig?: { id: number; name: string; subdomain: string } | null
    couponId?: number | null
    couponCode?: string | null
    /** Desconto do cupom, em centavos (não confundir com `discount`, que é percentual). */
    discountAmount?: number | null
    /** Desconto da promoção da loja, em centavos. */
    promotionDiscount?: number | null
    /** Entrega: o frete é snapshot do que o cliente pagou; o rastreio é manual. */
    shipping?: OrderShipping | null
    createdAt: string
    updatedAt: string
}

/**
 * A entrega do pedido de loja.
 *
 * `carrier`, `service`, `price` e `deliveryDays` são o frete COBRADO, congelado na venda —
 * não são editáveis. O que o operador preenche é `trackingCode`, `shippedAt` e
 * `deliveredAt`.
 */
export interface OrderShipping {
    id: number
    orderId: number
    carrier: string
    service: string
    price: number
    deliveryDays?: number | null
    freeShipping: boolean
    zipCode?: string | null
    trackingCode?: string | null
    /** Na retirada: pronto para retirar. Na entrega: despachado. */
    shippedAt?: string | null
    /** Na retirada: retirado. Na entrega: entregue. */
    deliveredAt?: string | null
    kind?: 'DELIVERY' | 'PICKUP'
    /** Quanto a transportadora cobra — o custo da etiqueta. */
    carrierPrice?: number | null
    serviceId?: number | null
    labelOrderId?: string | null
    labelStatus?: string | null
    labelPrice?: number | null
    labelUrl?: string | null
    labelPurchasedAt?: string | null
}

export interface UpdateOrderShippingData {
    trackingCode?: string | null
    shippedAt?: string | null
    deliveredAt?: string | null
}

export interface CreateOrderData {
    /**
     * Agendar sem gerar cobrança agora — o paciente paga no dia, na recepção.
     *
     * O valor do atendimento continua gravado; só a emissão espera, e sai depois pelo
     * "Gerar cobrança" da tela de detalhe. Não confundir com o orçamento, que ainda não
     * foi aprovado, nem com a forma "decidir na tela de pagamento", que EMITE a cobrança
     * e só deixa a forma em aberto.
     */
    deferBilling?: boolean
    /**
     * Local de atendimento confirmado no agendamento (snapshot de auditoria).
     * Sem isso, o backend deriva de parceiro > profissional.
     */
    street?: string
    number?: string
    complement?: string
    neighborhood?: string
    city?: string
    state?: string
    zipCode?: string
    country?: string
    addressSource?: 'PARTNER' | 'PROFESSIONAL' | 'HOSPITAL' | 'CUSTOM' | 'UNCONFIRMED'
    catalogId?: number
    items?: { catalogId: number; quantity: number; variationCatalogIds?: number[] }[]
    newCatalog?: { name: string }
    newCatalogProfessional?: {
        price?: number
        sellingPrice?: number
        markupPercentage?: number
    }
    newCatalogPartner?: {
        price?: number
        sellingPrice?: number
        markupPercentage?: number
    }
    status?: OrderStatus
    description?: string
    notes?: string
    scheduledAt?: string
    duration?: number
    room?: string
    roomId?: number
    equipment?: string
    preInstructions?: string
    postInstructions?: string
    clientId: number
    professionalId?: number
    partnerId?: number
    healthPlanId?: number
    hospitalId?: number
    /**
     * Cobrança em aberto do paciente onde SOMAR este procedimento, em vez de gerar
     * cobrança própria — quem agenda em vários lugares paga uma vez só.
     */
    billingTargetId?: number
    companyId: number
    paymentMethod?: string
    discount?: number
    pipelineId?: number
    anesthetistFee?: number
    /** Obrigatório quando há taxa: sem credor o repasse não tem para quem ir. */
    anesthetistRecipientKind?: 'HOSPITAL' | 'PROFESSIONAL' | 'PARTNER'
    anesthetistRecipientId?: number
    hospitalFee?: number
    hospitalFeeNotes?: string
    /** Override do valor de repasse ao profissional (centavos). null limpa; ausente = catálogo. */
    professionalAmount?: number | null
    validUntil?: string
    medicalSupplies?: {
        medicalSupplyId: number
        quantity: number
        /** Valor cobrado do paciente. */
        unitPrice: number
        /** Custo unitário pago a quem forneceu — obrigatório, é o que gera o repasse. */
        costPriceSnapshot?: number
        recipientKind?: 'SUPPLIER' | 'HOSPITAL' | 'PROFESSIONAL' | 'PARTNER'
        recipientId?: number
        notes?: string
    }[]
    exams?: { catalogId: number; quantity: number; unitPrice: number; complimentary?: boolean; notes?: string }[]
    consultations?: { catalogId: number; quantity: number; unitPrice: number; complimentary?: boolean; notes?: string }[]
    attachments?: any
    metadata?: any
    /** Por ordem de chegada: tem data, não tem hora marcada. */
    arrivalOrder?: boolean
}

export interface ApproveBudgetData {
    /** Local de atendimento confirmado na aprovação (snapshot no agendamento). */
    street?: string
    number?: string
    complement?: string
    neighborhood?: string
    city?: string
    state?: string
    zipCode?: string
    country?: string
    addressSource?: 'PARTNER' | 'PROFESSIONAL' | 'HOSPITAL' | 'CUSTOM' | 'UNCONFIRMED'
    scheduledAt?: string
    agendaId?: number
    roomId?: number
    paymentMethod?: string
    pipelineId?: number
    /**
     * Aprova em pagamento FLEX: não gera cobrança agora — o cliente quita em
     * parcelas avulsas pelo portal. Ver `Order.flexPayment`.
     */
    flex?: boolean
}

export interface UpdateOrderData
    extends Partial<Omit<CreateOrderData, 'healthPlanId'>> {
    /** `null` RETIRA o convênio. Ausente significa "não mexer neste campo". */
    healthPlanId?: number | null
    startedAt?: string
    completedAt?: string
    complications?: string
    outcome?: string
}

export async function getOrders(
    companyId?: number
): Promise<ApiResponse<Order[]>> {
    const params = companyId ? { companyId } : {}
    return apiRequest<Order[]>('orders', { params })
}

/**
 * @param params.packages 'exclude' esconde os procedimentos vindos de pacote (eles têm a tela
 *                        /medical/packages); 'only' devolve apenas eles.
 */
export async function getOrdersByCompany(
    companyId: number,
    params?: { page?: number; limit?: number; search?: string; status?: string; partnerId?: number; id?: number; cpf?: string; clientId?: number; createdById?: number; packages?: 'exclude' | 'only'; startDate?: string; endDate?: string; sortBy?: string; sortOrder?: string;
        /**
         * Janela de DATA DE PAGAMENTO (`billing.paidAt`), independente de
         * `startDate`/`endDate`, que filtram o registro. É o que o fechamento de caixa
         * usa: a venda de sexta paga na segunda pertence ao caixa da segunda.
         */
        paidStartDate?: string; paidEndDate?: string;
        /** Status da cobrança vinculada; 'NONE' = sem cobrança. */
        billingStatus?: string;
    }
): Promise<ApiResponse<Order[]>> {
    return apiRequest<Order[]>(`orders/company/${companyId}`, { params: params || {} })
}

export async function getOrdersByClient(
    clientId: number
): Promise<ApiResponse<Order[]>> {
    return apiRequest<Order[]>(`orders/client/${clientId}`, {})
}

export async function getOrdersByProfessional(
    professionalId: number
): Promise<ApiResponse<Order[]>> {
    return apiRequest<Order[]>(
        `orders/professional/${professionalId}`,
        {}
    )
}

export async function getOrder(
    id: number
): Promise<ApiResponse<Order>> {
    return apiRequest<Order>(`orders/${id}`, {})
}

export async function createOrder(
    data: CreateOrderData
): Promise<ApiResponse<Order>> {
    return apiRequest<Order>('orders', { data }, 'post')
}

export async function updateOrder(
    id: number,
    data: UpdateOrderData
): Promise<ApiResponse<Order>> {
    return apiRequest<Order>(`orders/${id}`, { data }, 'patch')
}

/**
 * Troca o local de atendimento já gravado no procedimento.
 *
 * Endpoint próprio, e não o `updateOrder`: a edição arrasta recálculo de preço e
 * cobrança complementar, que não têm nada a ver com corrigir uma rua.
 */
export async function updateOrderServiceAddress(
    id: number,
    data: ServiceAddressSnapshot
): Promise<ApiResponse<Order>> {
    return apiRequest<Order>(`orders/${id}/service-address`, { data }, 'patch')
}

export async function deleteOrder(id: number): Promise<ApiResponse<void>> {
    return apiRequest<void>(`orders/${id}`, {}, 'delete')
}

export async function uploadOrderAttachment(
    id: number,
    file: File,
    userName: string,
    description: string,
    details?: string
): Promise<ApiResponse<Order>> {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('userName', userName)
    formData.append('description', description)
    if (details) {
        formData.append('details', details)
    }

    const client = getClient(true)
    // A chave 'token' nunca existiu neste projeto — estas chamadas iam sem
    // credencial. O access token vive em memória.
    const token = getStoredToken()

    const response = await client.post(
        `orders/${id}/attachments`,
        formData,
        {
            headers: {
                'Content-Type': 'multipart/form-data',
                Authorization: `Bearer ${token}`,
            },
        }
    )

    return {
        success: true,
        result: response.data,
    }
}

export async function deleteOrderAttachment(
    id: number,
    attachmentIndex: number
): Promise<ApiResponse<void>> {
    return apiRequest<void>(
        `orders/${id}/attachments/${attachmentIndex}`,
        {},
        'delete'
    )
}

export async function addOrderTeamMember(
    orderId: number,
    professionalId: number,
    role?: string
): Promise<ApiResponse<OrderTeamMember>> {
    return apiRequest<OrderTeamMember>(
        `orders/${orderId}/team`,
        { data: { professionalId, role } },
        'post'
    )
}

export async function removeOrderTeamMember(
    orderId: number,
    memberId: number
): Promise<ApiResponse<void>> {
    return apiRequest<void>(
        `orders/${orderId}/team/${memberId}`,
        {},
        'delete'
    )
}

export async function printOrder(
    orderId: number,
    delivery: PdfDelivery = 'download'
): Promise<void> {
    const baseUrl = import.meta.env.VITE_API_BASE_URL || ''
    const token = getStoredToken() || ''

    const url = `${baseUrl}/orders/${orderId}/pdf`

    const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
    })

    if (!response.ok) throw new Error('Erro ao exportar procedimento em PDF')

    const blob = await response.blob()
    const filename = `procedimento-${orderId}.pdf`

    deliverPdf(blob, filename, delivery)
}

// === BILLING FUNCTIONS ===

export async function markOrderBillingAsPaid(
    id: number
): Promise<ApiResponse<void>> {
    return apiRequest<void>(`orders/${id}/billing/mark-paid`, {}, 'patch')
}

export async function cancelOrderBilling(
    id: number
): Promise<ApiResponse<void>> {
    return apiRequest<void>(`orders/${id}/billing/cancel`, {}, 'patch')
}

/**
 * Cancela o procedimento sem excluí-lo: ele continua na listagem com status
 * "Cancelado". A cobrança sem pagamento é cancelada junto; a cobrança paga vira
 * solicitação de estorno (`refundPending: true`).
 */
export async function cancelOrder(
    id: number,
    reason?: string,
): Promise<ApiResponse<CancelAppointmentResult>> {
    return apiRequest<CancelAppointmentResult>(
        `orders/${id}/cancel`,
        { data: { reason } },
        'patch',
    )
}

/** Transfere o vendedor (createdBy) do procedimento para outro usuário. */
export async function transferOrderSeller(
    id: number,
    sellerId: number,
): Promise<ApiResponse<Order>> {
    return apiRequest<Order>(
        `orders/${id}/transfer-seller`,
        { data: { sellerId } },
        'patch',
    )
}

/** Gera a cobrança de um procedimento que ficou sem cobrança. Retorna o id da cobrança criada. */
export async function generateOrderBilling(
    id: number,
    /**
     * Quanto cobrar agora, em centavos. Ausente = o saldo inteiro.
     *
     * Vai no gesto de CRIAR porque alterar o valor de uma cobrança atrelada a
     * atendimento é proibido — é por ali que sairia desconto sem autorização.
     */
    amount?: number,
): Promise<ApiResponse<{ billingId: number }>> {
    return apiRequest<{ billingId: number }>(
        `orders/${id}/generate-billing`,
        amount != null ? { data: { amount } } : {},
        'post',
    )
}

export const PROCEDURE_STATUS_LABELS: Record<OrderStatus, string> = {
    BUDGET: 'Orçamento',
    SCHEDULED: 'Agendado',
    IN_PROGRESS: 'Em Andamento',
    COMPLETED: 'Concluído',
    CANCELLED: 'Cancelado',
    POSTPONED: 'Adiado',
}

/**
 * Opções do filtro de status — não é o enum puro.
 *
 * `APPROVED` é sintético (o backend traduz para SCHEDULED sem data): "Aprovado" e
 * "Agendado" são o mesmo status no banco, e a lista já os separa no badge. Sem esta
 * opção, quem quer ver só o que falta marcar precisa varrer a lista inteira.
 */
export const PROCEDURE_STATUS_FILTER_OPTIONS: {
    value: string
    label: string
}[] = [
    { value: 'BUDGET', label: 'Orçamento' },
    { value: 'APPROVED', label: 'Aprovado' },
    { value: 'SCHEDULED', label: 'Agendado' },
    { value: 'IN_PROGRESS', label: 'Em Andamento' },
    { value: 'COMPLETED', label: 'Concluído' },
    { value: 'CANCELLED', label: 'Cancelado' },
    { value: 'POSTPONED', label: 'Adiado' },
]

export const PROCEDURE_STATUS_COLORS: Record<OrderStatus, string> = {
    BUDGET: 'badge-accent',
    SCHEDULED: 'badge-info',
    IN_PROGRESS: 'badge-warning',
    COMPLETED: 'badge-success',
    CANCELLED: 'badge-error',
    POSTPONED: 'badge-secondary',
}

/**
 * O status `SCHEDULED` só vira "Agendado" quando existe data marcada. Sem data,
 * o procedimento apenas saiu do orçamento — está aprovado, esperando a agenda.
 */
export function getOrderStatusLabel(
    status: OrderStatus,
    scheduledAt?: string | null,
): string {
    if (status === 'SCHEDULED' && !scheduledAt) return 'Aprovado'
    return PROCEDURE_STATUS_LABELS[status]
}

export function getOrderStatusColor(
    status: OrderStatus,
    scheduledAt?: string | null,
): string {
    if (status === 'SCHEDULED' && !scheduledAt) return 'badge-primary'
    return PROCEDURE_STATUS_COLORS[status]
}

// ==================== ORÇAMENTO ====================

export async function approveOrderBudget(
    orderId: number,
    data: ApproveBudgetData,
): Promise<ApiResponse<Order>> {
    return apiRequest<Order>(`orders/${orderId}/approve-budget`, { data }, 'patch')
}

/**
 * Ativa o pagamento flex num procedimento já aprovado (não flex): o cliente passa
 * a somar pagamentos avulsos pelo portal até fechar o total.
 *
 * ⚠️ **Não cancela nem mexe nas cobranças existentes** — elas são mantidas e
 * atreladas, e o saldo do flex já desconta o que elas representam
 * (`enableFlexPayment` no backend). Esta descrição dizia o contrário, e quem lia
 * daqui evitava a ação com medo de derrubar a cobrança do paciente.
 *
 * Recusa orçamento (aprove antes), cancelado e o que já é flex.
 */
export async function enableOrderFlexPayment(
    orderId: number
): Promise<ApiResponse<Order>> {
    return apiRequest<Order>(
        `orders/${orderId}/enable-flex`,
        {},
        'patch'
    )
}

/**
 * Estado da quitação com desconto — o que a confirmação precisa mostrar antes de
 * o operador decidir.
 */
export interface OrderSettlement {
    orderId: number
    /** Valor combinado do procedimento. */
    agreedAmount: number
    /** Soma efetivamente paga nas cobranças do procedimento. */
    paidAmount: number
    /** Diferença que vira desconto ao quitar. */
    discountAmount: number
    /** Total de repasse que a quitação libera — sai pelo valor cheio. */
    payoutAmount: number
    canSettle: boolean
    /** Por que não pode quitar; a tela mostra em vez de sumir com a ação. */
    reason: string | null
    /** Solicitação já aguardando aprovação, se houver. */
    pendingRequestId: number | null
}

export async function getOrderSettlement(
    orderId: number
): Promise<ApiResponse<OrderSettlement>> {
    return apiRequest<OrderSettlement>(
        `orders/${orderId}/settlement`,
        {},
        'get'
    )
}

/**
 * Solicita a quitação. NÃO quita: a diferença só vira desconto (e os repasses só
 * são liberados) quando alguém aprovar na fila de ações sensíveis.
 */
export async function requestOrderSettlement(
    orderId: number
): Promise<ApiResponse<{ requestId: number; discountAmount: number }>> {
    return apiRequest<{ requestId: number; discountAmount: number }>(
        `orders/${orderId}/settlement-request`,
        {},
        'post'
    )
}

/**
 * Atualiza a entrega do pedido de loja: rastreio, despacho e recebimento.
 *
 * Frete, transportadora e prazo NÃO mudam aqui — são o que o cliente pagou.
 */
export async function updateOrderShipping(
    orderId: number,
    data: UpdateOrderShippingData
): Promise<ApiResponse<OrderShipping>> {
    return apiRequest<OrderShipping>(`orders/${orderId}/shipping`, { data }, 'patch')
}

/**
 * Compra a etiqueta do Melhor Envio do pedido de loja (paga com o saldo da conta da loja)
 * e devolve a entrega com o link de impressão.
 */
export async function purchaseOrderLabel(
    orderId: number,
    data: { recipientDocument?: string; invoiceKey?: string }
): Promise<ApiResponse<OrderShipping>> {
    return apiRequest<OrderShipping>(`store-orders/${orderId}/label`, { data }, 'post')
}

/** Busca no Melhor Envio a situação e o rastreio da etiqueta. */
export async function refreshOrderLabelTracking(
    orderId: number
): Promise<ApiResponse<OrderShipping>> {
    return apiRequest<OrderShipping>(`store-orders/${orderId}/label/tracking`, {}, 'post')
}

/** Cancela a etiqueta antes da postagem (o Melhor Envio estorna em até 12 horas). */
export async function cancelOrderLabel(orderId: number): Promise<ApiResponse<OrderShipping>> {
    return apiRequest<OrderShipping>(`store-orders/${orderId}/label/cancel`, {}, 'post')
}

/** Renova a validade da guia: vencimento = hoje + dias configurados na empresa. */
export async function renewOrderGuide(orderId: number): Promise<ApiResponse<Order>> {
    return apiRequest<Order>(`orders/${orderId}/renew-guide`, {}, 'patch')
}

/**
 * Envia o link flex ao paciente pelo canal escolhido. O link também sai
 * automaticamente ao aprovar/ativar o flex — este é o reenvio manual.
 */
export async function sendOrderFlexLink(
    orderId: number,
    channel: 'whatsapp' | 'email'
): Promise<ApiResponse<{ message: string }>> {
    return apiRequest<{ message: string }>(
        `orders/${orderId}/send-flex-link`,
        { data: { channel } },
        'post'
    )
}

export async function exportOrderBudgetPdf(
    orderId: number,
    delivery: PdfDelivery = 'download'
): Promise<void> {
    const client = getClient()
    const response = await client.get(`orders/${orderId}/budget/pdf`, { responseType: 'blob' })
    deliverPdf(
        new Blob([response.data], { type: 'application/pdf' }),
        `orcamento-procedimento-${orderId}.pdf`,
        delivery
    )
}

/** Envia o documento do procedimento ao paciente, SEM valores. */
export async function sendOrderEmail(orderId: number): Promise<ApiResponse<void>> {
    return apiRequest<void>(`orders/${orderId}/send-email`, {}, 'post')
}

export async function sendOrderBudgetEmail(orderId: number): Promise<ApiResponse<void>> {
    return apiRequest<void>(`orders/${orderId}/budget/send-email`, {}, 'post')
}

export interface RescheduleAppointmentData {
    /** ISO. Com `arrivalOrder`, a hora é descartada no backend. */
    scheduledAt: string
    /** Por ordem de chegada: tem data, não tem hora. */
    arrivalOrder?: boolean
    reason?: string
}

/**
 * Remarca o atendimento: move a entidade E a agenda juntas, valida conflito de horário
 * (empresas de INTERMEDIAÇÃO não bloqueiam), grava o histórico e avisa o paciente da nova
 * data.
 *
 * Diferente de um `update` com data nova — que só sobrescrevia o campo, sem checar
 * conflito e sem avisar ninguém.
 */
/**
 * Remarca UM componente de uma venda de pacote.
 *
 * Diferente de `rescheduleOrder`, que move o atendimento inteiro: aqui só a linha e o
 * evento de agenda dela mudam — num pacote a cirurgia adia e a consulta de retorno fica.
 */
export async function rescheduleOrderComponent(
    orderId: number,
    componentId: number,
    data: RescheduleAppointmentData & { roomId?: number | null }
): Promise<ApiResponse<Order>> {
    return apiRequest<Order>(
        `orders/${orderId}/components/${componentId}/reschedule`,
        { data },
        'patch'
    )
}

export async function rescheduleOrder(
    id: number,
    data: RescheduleAppointmentData
): Promise<ApiResponse<Order>> {
    return apiRequest<Order>(`orders/${id}/reschedule`, { data }, 'patch')
}

/**
 * REMOVE a data do atendimento: o horário é liberado (o `AgendaEvent` é apagado) e o
 * status acompanha.
 *
 * O procedimento continua existindo — some da agenda e volta a ser o "aprovado
 * esperando agenda". Não confundir com cancelar: cobrança, itens e repasses ficam
 * intactos.
 */
export async function unscheduleOrder(
    id: number,
    data: { reason?: string } = {}
): Promise<ApiResponse<Order>> {
    return apiRequest<Order>(`orders/${id}/unschedule`, { data }, 'patch')
}

/**
 * Troca o status manualmente — a régua do operador sobre o que de fato aconteceu.
 * Só o status (e o evento da agenda) mudam: nada de cobrança, repasse ou aviso ao
 * paciente. `CANCELLED` é recusado pelo backend; cancelar tem ação própria.
 */
export async function changeOrderStatus(
    id: number,
    status: OrderStatus,
): Promise<ApiResponse<Order>> {
    return apiRequest<Order>(`orders/${id}/status`, { data: { status } }, 'patch')
}
